/**
 * Zustand store for backup/restore operations.
 * Tracks active operations and their progress.
 */

import { create } from 'zustand';
import type {
  ActiveBackupOperation,
  BackupTaskProgress,
  BackupTaskStatus,
} from '../types/backupRestore';

type BackupRestoreState = {
  /** Active backup/restore operations */
  operations: ActiveBackupOperation[];

  /** IDs of minimized operations (shown in status bar instead of dialog) */
  minimizedOperationIds: Set<string>;

  /** Add a new operation */
  addOperation: (operation: Omit<ActiveBackupOperation, 'id' | 'startedAt' | 'status'>) => string;

  /** Update operation status from task progress */
  updateOperationProgress: (taskId: string, progress: BackupTaskProgress) => void;

  /** Update operation by operation ID */
  updateOperation: (operationId: string, updates: Partial<ActiveBackupOperation>) => void;

  /** Remove a completed operation */
  removeOperation: (operationId: string) => void;

  /** Clear all completed operations */
  clearCompletedOperations: () => void;

  /** Get operation by ID */
  getOperation: (operationId: string) => ActiveBackupOperation | undefined;

  /** Get active operations (not completed) */
  getActiveOperations: () => ActiveBackupOperation[];

  /** Minimize an operation (hide dialog, show in status bar) */
  minimizeOperation: (operationId: string) => void;

  /** Restore a minimized operation (show dialog again) */
  restoreOperation: (operationId: string) => void;

  /** Check if an operation is minimized */
  isMinimized: (operationId: string) => boolean;

  /** Get all minimized operations */
  getMinimizedOperations: () => ActiveBackupOperation[];
};

function generateOperationId(): string {
  return `op-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function isTerminalStatus(status: BackupTaskStatus): boolean {
  return status === 'succeeded' || status === 'failed' || status === 'cancelled';
}

export const useBackupRestoreStore = create<BackupRestoreState>((set, get) => ({
  operations: [],
  minimizedOperationIds: new Set<string>(),

  addOperation: (operation) => {
    const id = generateOperationId();
    const newOperation: ActiveBackupOperation = {
      ...operation,
      id,
      status: 'created',
      startedAt: new Date().toISOString(),
    };

    set((state) => ({
      operations: [...state.operations, newOperation],
    }));

    return id;
  },

  updateOperationProgress: (taskId, progress) => {
    set((state) => ({
      operations: state.operations.map((op) => {
        if (op.taskId !== taskId) return op;

        const updates: Partial<ActiveBackupOperation> = {
          status: progress.status,
          progress: progress.progress,
          message: progress.message,
        };

        // Mark as completed if terminal status
        if (isTerminalStatus(progress.status)) {
          updates.completedAt = new Date().toISOString();
          if (progress.status === 'failed') {
            updates.error = progress.message;
          }
        }

        return { ...op, ...updates };
      }),
    }));
  },

  updateOperation: (operationId, updates) => {
    set((state) => ({
      operations: state.operations.map((op) =>
        op.id === operationId ? { ...op, ...updates } : op
      ),
    }));
  },

  removeOperation: (operationId) => {
    set((state) => {
      const newMinimized = new Set(state.minimizedOperationIds);
      newMinimized.delete(operationId);
      return {
        operations: state.operations.filter((op) => op.id !== operationId),
        minimizedOperationIds: newMinimized,
      };
    });
  },

  clearCompletedOperations: () => {
    set((state) => ({
      operations: state.operations.filter((op) => !isTerminalStatus(op.status)),
    }));
  },

  getOperation: (operationId) => {
    return get().operations.find((op) => op.id === operationId);
  },

  getActiveOperations: () => {
    return get().operations.filter((op) => !isTerminalStatus(op.status));
  },

  minimizeOperation: (operationId) => {
    set((state) => {
      const newSet = new Set(state.minimizedOperationIds);
      newSet.add(operationId);
      return { minimizedOperationIds: newSet };
    });
  },

  restoreOperation: (operationId) => {
    set((state) => {
      const newSet = new Set(state.minimizedOperationIds);
      newSet.delete(operationId);
      return { minimizedOperationIds: newSet };
    });
  },

  isMinimized: (operationId) => {
    return get().minimizedOperationIds.has(operationId);
  },

  getMinimizedOperations: () => {
    const { operations, minimizedOperationIds } = get();
    return operations.filter((op) => minimizedOperationIds.has(op.id));
  },
}));
